import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from '@/app/app';
import { readOAuthReturn } from '@/features/auth';
import { createApolloClient } from '@/graphql/client';
import { env } from '@/shared/config/env';
import { ROUTES } from '@/shared/config/routes';
import { initAnalytics, track } from '@/shared/lib/analytics';
import { notify } from '@/shared/lib/toast';
import './index.css';

const RELOADED_KEY = 'macswarm:preload-reloaded';

// A deploy replaced the chunks this tab was built against: reload once instead of breaking.
window.addEventListener('vite:preloadError', () => {
  if (sessionStorage.getItem(RELOADED_KEY)) return;
  sessionStorage.setItem(RELOADED_KEY, '1');
  window.location.reload();
});

async function start(): Promise<void> {
  initAnalytics();
  // A redirect sign-in (the popup was blocked) comes back marked; report it and clean the address bar.
  const oauthReturn = readOAuthReturn(window.location.href);
  if (oauthReturn) {
    track('login', { method: oauthReturn.provider });
    window.history.replaceState(window.history.state, '', oauthReturn.cleanUrl);
  }
  let isLeaving = false;
  const terminatingLink =
    import.meta.env.DEV && env.VITE_MOCK_API
      ? (await import('@/dev/mock-api')).createMockLink()
      : undefined;

  const client = createApolloClient({
    uri: env.VITE_API_URL,
    terminatingLink,
    onError: notify.error,
    onUnauthenticated: () => {
      if (isLeaving || window.location.pathname.startsWith(ROUTES.SIGN_IN)) return;
      isLeaving = true;
      void client.clearStore().then(() => window.location.assign(ROUTES.SIGN_IN));
    },
  });

  const root = document.getElementById('root');
  if (!root) throw new Error('The #root element is missing from index.html');
  createRoot(root).render(
    <StrictMode>
      <App client={client} />
    </StrictMode>,
  );
}

void start();
